import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@workspace/ui/lib/utils';

const buttonVariants = cva(
  "inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-normal transition-all disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground font-medium hover:bg-primary-hover',
        destructive:
          'bg-destructive border border-destructive text-white shadow-xs hover:bg-destructive/90',
        'destructive-light':
          'bg-destructive/10 text-destructive hover:bg-destructive/20 font-medium',
        outline: 'border border-gray-60 bg-gray-50 text-gray-550 hover:bg-gray-60/40 font-medium!',
        secondary:
          'bg-secondary border border-secondary text-primary shadow-xs hover:bg-secondary/80 font-medium',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        ghostSecondary: '',
        link: 'text-primary underline-offset-2 hover:underline',
        tertiary: 'bg-black text-white',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3 text-base w-auto',
        sm: 'h-8 text-sm gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 px-6 has-[>svg]:px-4 text-base',
        xl: 'h-12 px-8 has-[>svg]:px-6 text-lg',
        icon: 'size-9',
        'icon-sm': 'size-7',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, className, size }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
